// ============================================
// TICKER INPUT COMPONENT
// Search box with popular tickers
// ============================================

import React, { useState, useRef, useEffect } from 'react';

interface TickerInputProps {
  onAnalyze: (ticker: string) => void;
  disabled?: boolean;
}

const POPULAR_TICKERS = [
  { symbol: 'SPY', name: 'S&P 500 ETF' },
  { symbol: 'QQQ', name: 'Nasdaq 100 ETF' },
  { symbol: 'AAPL', name: 'Apple' },
  { symbol: 'MSFT', name: 'Microsoft' },
  { symbol: 'NVDA', name: 'NVIDIA' },
  { symbol: 'TSLA', name: 'Tesla' },
  { symbol: 'AMZN', name: 'Amazon' },
  { symbol: 'META', name: 'Meta' },
  { symbol: 'GOOGL', name: 'Alphabet' },
  { symbol: 'AMD', name: 'AMD' },
];

export const TickerInput: React.FC<TickerInputProps> = ({ onAnalyze, disabled }) => {
  const [value, setValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (value.trim() && !disabled) {
      onAnalyze(value.trim().toUpperCase());
    }
  };

  const handleQuickSelect = (ticker: string) => {
    setValue(ticker);
    onAnalyze(ticker);
  };

  // Auto-focus on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <div className="space-y-8">
      {/* Main Input */}
      <form onSubmit={handleSubmit} className="relative">
        <div className={`
          relative rounded-2xl overflow-hidden transition-all duration-300
          ${isFocused ? 'ring-2 ring-cyan-500/50 shadow-lg shadow-cyan-500/10' : ''}
        `}>
          {/* Glow effect */}
          {isFocused && (
            <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-emerald-500/5 to-cyan-500/5 animate-pulse" />
          )}

          <div className="relative flex items-center bg-terminal-card border-2 border-terminal-border rounded-2xl">
            {/* Prefix */}
            <div className="pl-6 pr-2 text-terminal-dim text-2xl sm:text-3xl font-bold">
              $
            </div>

            {/* Input */}
            <input
              ref={inputRef}
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value.toUpperCase())}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              placeholder="ENTER TICKER"
              disabled={disabled}
              className="
                flex-1 bg-transparent py-6 sm:py-7 text-3xl sm:text-4xl font-bold text-terminal-bright
                placeholder:text-terminal-border outline-none tracking-wider
                disabled:opacity-50
              "
              maxLength={5}
              autoComplete="off"
              spellCheck={false}
            />

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!value.trim() || disabled}
              className="
                m-2.5 px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-cyan-500 to-emerald-500
                text-black font-bold text-lg sm:text-xl rounded-xl transition-all
                hover:from-cyan-400 hover:to-emerald-400
                disabled:opacity-30 disabled:cursor-not-allowed
                flex items-center gap-2
              "
            >
              <span>ANALYZE</span>
              <span className="text-xl">→</span>
            </button>
          </div>
        </div>

        {/* Keyboard hint */}
        <div className="absolute -bottom-7 right-0 text-sm text-terminal-dim">
          Press <kbd className="px-2 py-1 bg-terminal-border rounded text-terminal-bright">Enter</kbd> to analyze
        </div>
      </form>

      {/* Popular Tickers */}
      <div className="pt-4">
        <div className="text-sm text-terminal-dim mb-4 flex items-center gap-2">
          <span className="text-cyan-400">▸</span>
          POPULAR TICKERS
        </div>

        <div className="flex flex-wrap gap-2.5">
          {POPULAR_TICKERS.map(({ symbol, name }) => (
            <button
              key={symbol}
              onClick={() => handleQuickSelect(symbol)}
              disabled={disabled}
              className="
                group px-5 py-3 bg-terminal-card border border-terminal-border rounded-xl
                hover:border-cyan-500/50 hover:bg-cyan-500/5 transition-all
                disabled:opacity-30 disabled:cursor-not-allowed
                text-base
              "
            >
              <span className="text-terminal-bright font-bold text-base">{symbol}</span>
              <span className="text-terminal-dim text-sm ml-2 hidden sm:inline group-hover:text-cyan-400 transition-colors">
                {name}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
